import NetworkError from './NetworkError.js';
import ProgressTimeout from './ProgressTimeout.js';
import toError from './toError.js';
const noop = () => { };
/**
 * Folds header names that differ only in case into a single entry, last one
 * wins.
 *
 * Per the XHR spec, `setRequestHeader()` *combines* values when it is called
 * twice with names that are a byte-case-insensitive match, so passing both
 * `Content-Type` and `content-type` would send `Content-Type: a, b` rather
 * than the intended `b`. Callers merge header objects from several sources
 * (defaults, user options, a request signer), and plain objects have
 * case-sensitive keys, so such a clash is easy to produce by accident — and
 * for a signed request it turns into an opaque signature mismatch.
 */
function dedupeHeaders(headers) {
    const byLowerCaseName = new Map();
    for (const [name, value] of Object.entries(headers)) {
        byLowerCaseName.set(name.toLowerCase(), [name, value]);
    }
    return byLowerCaseName.values();
}
/**
 * Fetches data from a specified URL using XMLHttpRequest, with optional retry functionality and progress tracking.
 *
 * @param url The URL to send the request to.
 * @param options Optional settings for the fetch operation.
 */
export function fetcher(url, options = {}) {
    const { body = null, headers = {}, method = 'GET', onBeforeRequest = noop, onUploadProgress = noop, shouldRetry = () => true, onAfterResponse = noop, onTimeout = noop, responseType, retries = 3, signal = null, timeout = 30_000, withCredentials = false, } = options;
    // 300 ms, 600 ms, 1200 ms, 2400 ms, 4800 ms
    const delay = (attempt) => 0.3 * 2 ** (attempt - 1) * 1000;
    const timer = new ProgressTimeout(timeout, onTimeout);
    function requestWithRetry(retryCount = 0) {
        // biome-ignore lint/suspicious/noAsyncPromiseExecutor: it's fine
        return new Promise(async (resolve, reject) => {
            const xhr = new XMLHttpRequest();
            const onError = (error) => {
                if (shouldRetry(xhr) && retryCount < retries) {
                    setTimeout(() => {
                        requestWithRetry(retryCount + 1).then(resolve, reject);
                    }, delay(retryCount));
                }
                else {
                    timer.done();
                    reject(error);
                }
            };
            xhr.open(method, url, true);
            xhr.withCredentials = withCredentials;
            if (responseType) {
                xhr.responseType = responseType;
            }
            xhr.onload = async () => {
                try {
                    await onAfterResponse(xhr, retryCount);
                }
                catch (err) {
                    // This is important as we need to emit the xhr
                    // over the upload-error event.
                    onError(Object.assign(toError(err), { request: xhr }));
                    return;
                }
                if (xhr.status >= 200 && xhr.status < 300) {
                    timer.done();
                    resolve(xhr);
                }
                else if (shouldRetry(xhr) && retryCount < retries) {
                    setTimeout(() => {
                        requestWithRetry(retryCount + 1).then(resolve, reject);
                    }, delay(retryCount));
                }
                else {
                    timer.done();
                    reject(new NetworkError(xhr.statusText, xhr));
                }
            };
            xhr.onerror = () => onError(new NetworkError(xhr.statusText, xhr));
            xhr.upload.onprogress = (event) => {
                timer.progress();
                onUploadProgress(event);
            };
            for (const [name, value] of dedupeHeaders(headers)) {
                xhr.setRequestHeader(name, value);
            }
            function abort() {
                xhr.abort();
                // Using DOMException for abort errors aligns with
                // the convention established by the Fetch API.
                reject(new DOMException('Aborted', 'AbortError'));
            }
            signal?.addEventListener('abort', abort);
            if (signal?.aborted) {
                // in case the signal was already aborted
                abort();
                return;
            }
            await onBeforeRequest(xhr, retryCount);
            xhr.send(body);
        });
    }
    return requestWithRetry();
}
